import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ONDERWERP_OPTIES = [
  { value: 'hulp', label: 'Ik zoek hulp voor mezelf of mijn kind' },
  { value: 'training', label: 'Aanmelden voor een training' },
  { value: 'groepstraining', label: 'Groepstraining op locatie aanvragen (school/gemeente)' },
  { value: 'vrijwilliger', label: 'Vrijwilliger worden' },
  { value: 'donateur', label: 'Donateur worden' },
  { value: 'bedrijfspartner', label: 'Bedrijfspartner worden' },
  { value: 'ambassadeur', label: 'Ambassadeur worden' },
  { value: 'samenwerking', label: 'Samenwerking (gemeente/school)' },
  { value: 'anders', label: 'Anders / Algemene vraag' },
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    organisatie: '',
    onderwerp: '',
    bericht: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    const param = searchParams.get('onderwerp');
    if (param) {
      const match = ONDERWERP_OPTIES.find(o => o.value === param);
      if (match) {
        setFormData(prev => ({ ...prev, onderwerp: match.value }));
      }
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const body = new URLSearchParams({
        naam: formData.naam,
        email: formData.email,
        telefoon: formData.telefoon,
        organisatie: formData.organisatie,
        onderwerp: ONDERWERP_OPTIES.find(o => o.value === formData.onderwerp)?.label ?? formData.onderwerp,
        bericht: formData.bericht,
      });

      const response = await fetch('https://readdy.ai/api/form/d76qhbbq7u9ee9hcek6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        body: body.toString(),
      });

      if (response.ok || response.status === 201 || response.status === 302) {
        setSubmitMessage('Bedankt! Uw bericht is succesvol verzonden.');
        setFormData({
          naam: '',
          email: '',
          telefoon: '',
          organisatie: '',
          onderwerp: '',
          bericht: ''
        });
      } else {
        const errorText = await response.text().catch(() => '');
        console.error('Form error:', response.status, errorText);
        setSubmitMessage(`Er is een fout opgetreden (${response.status}). Probeer het opnieuw of mail naar info@herstart.nl.`);
      }
    } catch (error) {
      console.error('Form submit exception:', error);
      setSubmitMessage('Verbindingsfout. Probeer het opnieuw of neem direct contact op via info@herstart.nl.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-32 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/f58ee852363a3266f189ebe177afcdf9.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50"></div>
          <div className="relative container mx-auto px-6">
            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">Contact</h1>
              <p className="text-xl md:text-2xl text-gray-100 leading-relaxed">
                Klaar om uw organisatie te transformeren? Neem contact met ons op voor een vrijblijvend gesprek.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-16">
                {/* Contact Form */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">Stuur ons een bericht</h2>
                  
                  <form 
                    onSubmit={handleSubmit}
                    data-readdy-form="herstart-contact"
                    className="space-y-6"
                  >
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="naam" className="block text-sm font-medium text-gray-700 mb-2">
                          Naam *
                        </label>
                        <input
                          type="text"
                          id="naam"
                          name="naam"
                          value={formData.naam}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          placeholder="Uw volledige naam"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          E-mail *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          placeholder="uw.email@adres.nl"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="telefoon" className="block text-sm font-medium text-gray-700 mb-2">
                          Telefoon
                        </label>
                        <input
                          type="tel"
                          id="telefoon"
                          name="telefoon"
                          value={formData.telefoon}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          placeholder="06 12345678"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="organisatie" className="block text-sm font-medium text-gray-700 mb-2">
                          Organisatie
                        </label>
                        <input
                          type="text"
                          id="organisatie"
                          name="organisatie"
                          value={formData.organisatie}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          placeholder="Uw bedrijfs- of organisatienaam"
                        />
                      </div>
                    </div>

                    {/* Onderwerp dropdown */}
                    <div>
                      <label htmlFor="onderwerp" className="block text-sm font-medium text-gray-700 mb-2">
                        Onderwerp *
                      </label>
                      <select
                        id="onderwerp"
                        name="onderwerp"
                        value={formData.onderwerp}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white cursor-pointer"
                      >
                        <option value="">Kies een onderwerp...</option>
                        {ONDERWERP_OPTIES.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="bericht" className="block text-sm font-medium text-gray-700 mb-2">
                        Bericht *
                      </label>
                      <textarea
                        id="bericht"
                        name="bericht"
                        value={formData.bericht}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
                        placeholder="Vertel ons over uw vraag of hoe wij kunnen helpen..."
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        {formData.bericht.length}/500 karakters
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-orange-500 text-white py-4 px-8 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? 'Bezig met verzenden...' : 'Verstuur bericht'}
                    </button>

                    {submitMessage && (
                      <div className={`p-4 rounded-lg ${
                        submitMessage.includes('succesvol') 
                          ? 'bg-green-50 text-green-800 border border-green-200' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                  </form>
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">Contactgegevens</h2>
                  
                  <div className="space-y-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <i className="ri-mail-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">E-mail</h3>
                        <p className="text-gray-600">info@herstart.nl</p>
                        <p className="text-sm text-gray-500 mt-1">
                          We reageren binnen 24 uur
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <i className="ri-whatsapp-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                        <p className="text-gray-600">+31 6 16708612</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Stuur ons een berichtje
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <i className="ri-phone-line text-orange-500 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Telefoon</h3>
                        <p className="text-gray-600">+31 6 16708612</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Ma-Vr 9:00 - 17:00
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <i className="ri-map-pin-line text-orange-500 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Locatie</h3>
                        <p className="text-gray-600">
                          Amsterdam, Nederland
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Op afspraak
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <i className="ri-time-line text-orange-500 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Responstijd</h3>
                        <p className="text-gray-600">
                          Binnen 24 uur
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Meestal veel sneller
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CTA Box */}
                  <div className="mt-12 bg-gradient-to-r from-orange-50 to-amber-50 p-8 rounded-xl border border-orange-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Klaar voor transformatie?
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Ontdek hoe wij uw organisatie kunnen helpen groeien en innoveren. 
                      Plan een vrijblijvend gesprek.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <a
                        href="tel:+31616708612"
                        className="inline-flex items-center px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-phone-line mr-2"></i>
                        Bel direct
                      </a>
                      <a
                        href="https://wa.me/31616708612"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line mr-2"></i>
                        WhatsApp
                      </a>
                      <a
                        href="mailto:info@herstart.nl"
                        className="inline-flex items-center px-6 py-3 bg-white text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-mail-line mr-2"></i>
                        E-mail ons
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Veelgestelde vragen
                </h2>
                <p className="text-xl text-gray-600">
                  Antwoorden op de meest gestelde vragen over onze diensten
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Hoe snel kunnen jullie starten?
                  </h3>
                  <p className="text-gray-600">
                    We kunnen meestal binnen 1-2 weken starten, afhankelijk van de scope van het project. 
                    Voor urgente projecten kunnen we vaak sneller schakelen.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Wat zijn jullie tarieven?
                  </h3>
                  <p className="text-gray-600">
                    Onze tarieven zijn afhankelijk van de complexiteit en scope van het project. 
                    We bieden altijd een vrijblijvende offerte aan na een intakegesprek.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Werken jullie ook met kleine bedrijven?
                  </h3>
                  <p className="text-gray-600">
                    Ja, we werken met organisaties van alle groottes. Van startups tot grote ondernemingen, 
                    iedereen verdient professionele ondersteuning.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Bieden jullie ook ondersteuning na oplevering?
                  </h3>
                  <p className="text-gray-600">
                    Absoluut! We bieden verschillende onderhouds- en supportpakketten aan om ervoor te zorgen 
                    dat uw systemen optimaal blijven functioneren.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
